// Screen-pop gate for the pooled dialer: when a bridge must throw the agent
// onto the contact card, and — when it must not — which rule stopped it.
//
// The gate reads a member state fed by two independently flaky sources (the
// SSE snapshot and the membership poll), so the member is null whenever
// neither has data at that instant. Those gaps are not states. A stream flap
// or one empty poll between calls must never read as "this session has no
// history", because that is exactly what a genuine mid-call page reload looks
// like — and the reload rule deliberately suppresses the pop.

import type { PooledDialerMemberState } from '../../../shared/types/pooled-dialer';

export type PoolScreenPopOutcome =
  /** The contact route committed and the card is on screen. */
  | 'navigated'
  /** The bridge landed while the agent was already on that contact card. */
  | 'already_there'
  /** Busy was the first state this browser session saw: the agent reloaded
   * mid-call and stays where they put themselves. */
  | 'mid_call_reload'
  /** The busy tenure ended without the server ever naming a contact, so there
   * was never anywhere to pop to. */
  | 'no_contact_id'
  /** The push was issued but the contact route never committed before the
   * tenure ended or the agent went elsewhere — the agent was live with a
   * prospect and the card never arrived. */
  | 'card_not_reached';

/** Which transport delivered the member state that resolved the pop:
 * the SSE pool stream, or the membership poll that covers stream-down
 * windows. */
export type PoolScreenPopSource = 'stream' | 'poll';

export type PoolBusyEntry =
  /** A fresh bridge — chime, then pop once the contact id resolves. */
  | 'bridged'
  | 'mid_call_reload'
  /** Already inside a busy tenure: a re-render, or a delivery gap that
   * momentarily blanked the member. */
  | 'ongoing';

/**
 * Folds one member observation into the last state actually seen. A null
 * observation carries no information — the delivery gap it stands for is
 * indistinguishable from a healthy quiet moment — so the last seen state
 * stands. This is what keeps a null last-observed meaning "never seen in this
 * session" for classifyBusyEntry.
 *
 * Known limit: this never expires, so an entire tenure passing unobserved
 * leaves `busy` standing and classifies the next bridge `ongoing`, which does
 * not pop. That needs a gap longer than the 3s membership poll to reach, and
 * it is no worse than treating the gap as a reload, which also does not pop.
 */
export function lastObservedState(
  previous: PooledDialerMemberState | null,
  observation: PooledDialerMemberState | null
): PooledDialerMemberState | null {
  return observation ?? previous;
}

/**
 * The contact the server last named for this member, folded exactly the way
 * lastObservedState folds the state — and for the same reason. A delivery gap
 * blanks the whole member, so reading its null contact id as "the server no
 * longer names anyone" would cancel a pop's retry for the rest of the call,
 * and the weak link that drops a navigation is the one that flaps delivery.
 *
 * Unlike the state, this is cleared by any real non-busy observation, so a
 * folded id can never outlive the call it belongs to and be compared against
 * the next tenure's pop.
 */
export function lastObservedContactId(
  previous: string | null,
  state: PooledDialerMemberState | null,
  observation: string | null
): string | null {
  if (state !== null && state !== 'busy') return null;
  return observation ?? previous;
}

/**
 * What a `busy` observation means, given every state seen before it in this
 * browser session.
 */
export function classifyBusyEntry(previous: PooledDialerMemberState | null): PoolBusyEntry {
  if (previous === null) return 'mid_call_reload';
  if (previous === 'busy') return 'ongoing';
  return 'bridged';
}

/**
 * The member just moved into manual work on a contact this session has not
 * already been redirected for. Reads the same observed-state machine as the
 * pop: a null previous is a mid-call reload, which must not yank the agent
 * anywhere, and a gap mid-handoff must not re-fire the redirect.
 */
export function manualHandoffStarted(
  previous: PooledDialerMemberState | null,
  state: PooledDialerMemberState | null,
  contactId: string | null,
  alreadyRedirectedContactId: string | null
): boolean {
  if (state !== 'manual' || previous === null || previous === 'manual') return false;
  return contactId !== null && contactId !== alreadyRedirectedContactId;
}

/**
 * How stale the pool snapshot was when the client applied a state, in the
 * client's own clock — both stamps come from this browser, so a constant
 * offset from the server cancels out. Null before any data has arrived
 * (`updatedAtMs === 0`), matching the initial-load convention the staleness
 * gates use: an unknown age, never an age of "since the epoch".
 *
 * Deliberately NOT a server-to-client subtraction. Clocks in this fleet drift
 * by minutes and get stepped by NTP mid-shift, so any cross-clock difference
 * measures skew at least as much as it measures delivery.
 *
 * Same-clock cancels drift but not a STEP: an NTP correction landing between
 * the two readings lands here in full. Treat negative or implausibly large
 * values as steps and discard them rather than reading them as staleness.
 */
export function snapshotAgeMs(nowMs: number, updatedAtMs: number): number | null {
  return updatedAtMs === 0 ? null : nowMs - updatedAtMs;
}

/**
 * How long the server took to publish a transition, from the transition stamp
 * to the server clock on the payload carrying it. Both readings come from the
 * server, so this is immune to agent clock skew and to how long the telemetry
 * batch waited before upload — unlike anything derived from occurred_at.
 *
 * A floor of roughly the broadcast debounce is expected; large values mean the
 * client was handed a view of the floor that was already old when it left the
 * server. Null when either stamp is missing or unparseable.
 */
export function publicationLagMs(
  stateChangedAt: string | null,
  serverAt: string | null
): number | null {
  if (!stateChangedAt || !serverAt) return null;
  const changedAtMs = Date.parse(stateChangedAt);
  const publishedAtMs = Date.parse(serverAt);
  if (!Number.isFinite(changedAtMs) || !Number.isFinite(publishedAtMs)) return null;
  return publishedAtMs - changedAtMs;
}

// ----------------------------------------------------------------
// Nav retry
// ----------------------------------------------------------------
//
// The pop is one `router.push` per busy tenure, and the push is latched the
// instant it is issued — before the route commits. App Router gives the client
// no failure signal: a push whose RSC fetch is dropped mid-flight (the agent's
// uplink, the same one that produces "Page Unreachable") simply never commits,
// the pathname never changes, and nothing re-asks. The agent then spends the
// whole call on the pool page with no contact card. That is the shape behind
// the card_not_reached events, and every one of them resolves only when the
// tenure ends — far too late for a retry keyed off the outcome.
//
// So the retry is armed on a timer while the tenure is still live, and the
// decision below is what it consults.

/** Pushes per busy tenure, the first included: one retry each at 2.5s and 5s,
 * then the client stops asking. Bounded on purpose — a dead uplink must not
 * produce an unbounded navigation loop, and past ~7.5s the failure is the link
 * rather than a lost race, with the live stage already carrying the name and
 * number the agent needs. */
export const SCREEN_POP_MAX_NAV_ATTEMPTS = 3;

/** How long a push gets to commit before it is treated as lost. Measured
 * navRenderMs is 303ms at p50 and 567ms at p90, so this is ~4x p90: a healthy
 * navigation is long finished and no retry ever fires behind it. Short enough
 * to still land inside a real conversation — median talk time on the calls
 * that lost the race was 10 seconds. */
export const SCREEN_POP_RETRY_DELAY_MS = 2_500;

export type ScreenPopRetryDecision =
  /** The route never committed and the conditions to ask again all hold. */
  | 'retry'
  /** The card is on screen; the pathname effect owns the outcome. */
  | 'arrived'
  /** Nothing left to pop to, or re-pushing would fight the agent. */
  | 'give_up';

/**
 * Whether a pop whose route has not committed yet may be pushed again.
 *
 * `state` is the last state this session actually SAW (nulls folded by
 * lastObservedState) — a delivery gap is not the end of a tenure, and reading
 * it as one would drop the retry exactly when the stream is struggling.
 *
 * Two rules keep the retry from fighting the agent rather than the network:
 * the server must still name the contact the push was issued for (a new
 * tenure owns its own pop), and the browser must still be sitting where the
 * push was issued from. A pathname that moved anywhere else is the agent
 * navigating deliberately mid-call, and yanking them back is worse than the
 * missing card.
 */
export function screenPopRetryDecision(input: {
  state: PooledDialerMemberState | null;
  /** Contact the server currently names for this member. */
  contactId: string | null;
  /** Contact the pending push was issued for. */
  targetContactId: string;
  /** Route the push aimed at. */
  target: string;
  /** Route the browser is on now. */
  pathname: string;
  /** Route the browser was on when the push was issued. */
  pushedFrom: string;
  /** Pushes issued for this tenure so far, the first included. */
  navAttempts: number;
}): ScreenPopRetryDecision {
  if (input.pathname === input.target) return 'arrived';
  if (input.state !== 'busy') return 'give_up';
  if (input.contactId !== input.targetContactId) return 'give_up';
  if (input.pathname !== input.pushedFrom) return 'give_up';
  if (input.navAttempts >= SCREEN_POP_MAX_NAV_ATTEMPTS) return 'give_up';
  return 'retry';
}
